import { api } from "@Aspecto/backend/convex/_generated/api";
import { SignIn, useAuth, useClerk, useSignIn, useUser } from "@clerk/tanstack-react-start";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import {
  ContinueAsSession,
  OrgBrandingHeader,
  WorkspaceNotFoundCard,
} from "@/components/auth/org-subdomain-states";
import {
  ConsumerEmailBlockedCard,
  WorkEmailError,
  guardWorkEmailSubmit,
  isConsumerEmail,
} from "@/components/auth/work-email-guard";
import { useRedirectAfterAuth } from "@/hooks/use-redirect-after-auth";
import { invalidateClientClerkAuthCache } from "@/routes/__root";

type AuthSearch = {
  redirect?: string;
  setup?: "complete";
};

// Per-tab marker that a sign-in attempt is in progress (survives the remount
// after the email-code step). See usage in SignInPage.
const ACTIVE_SIGN_IN_KEY = "afinio_active_signin";

export const Route = createFileRoute("/sign-in/")({
  validateSearch: (raw: Record<string, unknown>): AuthSearch => ({
    redirect: typeof raw.redirect === "string" ? raw.redirect : undefined,
    setup: raw.setup === "complete" ? "complete" : undefined,
  }),
  beforeLoad: async ({ context }) => {
    // Already signed in on the app origin → resolve which org to enter. On a
    // subdomain we fall through to the page (org sign-in / "continue as session").
    if (context.userId && !context.subdomain) {
      throw redirect({ to: "/select-org" });
    }
  },
  component: SignInPage,
});

function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <g fill="none" fillRule="evenodd">
        <path
          d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
          fill="#4285F4"
        />
        <path
          d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
          fill="#34A853"
        />
        <path
          d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
          fill="#FBBC05"
        />
        <path
          d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"
          fill="#EA4335"
        />
      </g>
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 21 21"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#00a4ef" />
      <rect x="1" y="11" width="9" height="9" fill="#7fba00" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

const clerkEmailOnlyAppearance = {
  elements: {
    rootBox: { width: "100%" },
    cardBox: { width: "100%", boxShadow: "none", overflow: "visible" },
    card: {
      padding: "0",
      boxShadow: "none",
      border: "none",
      backgroundColor: "transparent",
      gap: "0",
      width: "100%",
      overflow: "visible",
    },
    header: { display: "none" },
    socialButtonsRoot: { display: "none" },
    dividerRow: { display: "none" },
    footer: { display: "none" },
    lastAuthenticationStrategyBadge: { display: "none" },
    main: { padding: "0 2px" },
    formFieldInput: { boxShadow: "none" },
  },
};

function SignInPage() {
  const { isLoaded, isSignedIn } = useAuth();
  const { isLoaded: isUserLoaded, user: clerkUser } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const router = useRouter();
  const { isLoaded: signInLoaded, signIn } = useSignIn();
  const [oauthLoading, setOauthLoading] = useState<"oauth_google" | "oauth_microsoft" | null>(null);
  const [workEmailError, setWorkEmailError] = useState("");
  const { subdomain } = Route.useRouteContext();
  const { setup } = Route.useSearch();
  const { data: subdomainCompany } = useQuery(
    convexQuery(api.companies.getBySubdomain, subdomain ? { subdomain } : "skip"),
  );
  const clerkEmail = clerkUser?.primaryEmailAddress?.emailAddress;
  const isSignedInWithConsumerEmail = isUserLoaded && isSignedIn && isConsumerEmail(clerkEmail);
  const autoContinueAfterSetup = setup === "complete";

  const wasSignedInOnMountRef = useRef<boolean | null>(null);
  if (wasSignedInOnMountRef.current === null && isLoaded) {
    wasSignedInOnMountRef.current = isSignedIn;
  }
  // A sign-in attempt was started in this tab (email code or OAuth). We persist
  // it because after the email-code step Clerk redirects back to /sign-in, which
  // remounts this page with an active session — without the flag the mount-time
  // "was signed in" ref would be true and wrongly show "You're already signed
  // in". A fresh sign-in must go straight to the app; only a genuine
  // already-signed-in *arrival* (no attempt) shows the banner.
  const activeSignInRef = useRef(
    typeof window !== "undefined" && sessionStorage.getItem(ACTIVE_SIGN_IN_KEY) === "1",
  );
  const markSignInStarted = () => {
    try {
      sessionStorage.setItem(ACTIVE_SIGN_IN_KEY, "1");
    } catch {
      // storage unavailable — the flag is best-effort
    }
    activeSignInRef.current = true;
  };
  useEffect(() => {
    // Consume the flag once signed in; the redirect to the app takes over.
    if (isLoaded && isSignedIn && activeSignInRef.current) {
      try {
        sessionStorage.removeItem(ACTIVE_SIGN_IN_KEY);
      } catch {
        // ignore
      }
    }
  }, [isLoaded, isSignedIn]);

  const showContinueAsSession =
    !!subdomain &&
    !!subdomainCompany &&
    !!wasSignedInOnMountRef.current &&
    !activeSignInRef.current &&
    isSignedIn &&
    !autoContinueAfterSetup &&
    !isSignedInWithConsumerEmail;

  // On an org subdomain, sign-in lands directly in that org's app. On the
  // primary entry point (app.afinio.co) we don't know the org yet, so route to
  // /select-org which redirects to the user's org (or shows an org picker).
  const postAuthTarget = subdomain ? "/app" : "/select-org";
  useRedirectAfterAuth(
    postAuthTarget,
    !showContinueAsSession && !(isSignedIn && (!isUserLoaded || isSignedInWithConsumerEmail)),
  );

  const signInRef = useRef(signIn);
  signInRef.current = signIn;

  const status = signIn?.status;
  const currentStrategy = signIn?.firstFactorVerification?.strategy;

  useEffect(() => {
    if (!signInLoaded) return;
    const si = signInRef.current;
    if (!si) return;
    if (si.status !== "needs_first_factor") return;
    if (si.firstFactorVerification?.strategy !== "password") return;

    const emailCodeFactor = si.supportedFirstFactors?.find((f) => f.strategy === "email_code");
    if (!emailCodeFactor || !("emailAddressId" in emailCodeFactor)) return;

    void si.prepareFirstFactor({
      strategy: "email_code",
      emailAddressId: emailCodeFactor.emailAddressId as string,
    });
  }, [signInLoaded, status, currentStrategy]);

  const showSpinner =
    !isLoaded ||
    (isSignedIn && !isUserLoaded) ||
    (isSignedIn && !showContinueAsSession && !isSignedInWithConsumerEmail) ||
    (!!subdomain && subdomainCompany === undefined);

  if (showSpinner) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
      </div>
    );
  }

  if (subdomain && subdomainCompany === null) {
    return <WorkspaceNotFoundCard />;
  }

  if (isSignedInWithConsumerEmail) {
    return (
      <ConsumerEmailBlockedCard
        email={clerkEmail}
        onSignOut={() => void signOut({ redirectUrl: "/sign-in" })}
      />
    );
  }

  if (showContinueAsSession) {
    return (
      <ContinueAsSession
        email={clerkUser?.primaryEmailAddress?.emailAddress}
        onContinue={() => {
          invalidateClientClerkAuthCache();
          void router.invalidate().then(() => navigate({ to: "/app" }));
        }}
        onSwitchAccount={() => void signOut()}
      />
    );
  }

  const handleOAuth = async (strategy: "oauth_google" | "oauth_microsoft") => {
    if (!signIn || oauthLoading) return;
    markSignInStarted();
    setOauthLoading(strategy);
    try {
      await signIn.authenticateWithRedirect({
        strategy,
        redirectUrl: "/sign-in/sso-callback",
        redirectUrlComplete: postAuthTarget,
      });
    } catch {
      setOauthLoading(null);
    }
  };

  return (
    <div className="flex flex-1 items-center justify-center px-4">
      <div className="w-full max-w-[25rem] rounded-2xl border bg-card px-8 py-10 shadow-sm">
        {subdomainCompany ? (
          <OrgBrandingHeader company={subdomainCompany} />
        ) : (
          <div className="mb-6 text-center">
            <h1 className="text-xl font-semibold tracking-tight">Sign in to Afinio</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Welcome back. Please sign in with your work email
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2.5">
          <button
            onClick={() => void handleOAuth("oauth_google")}
            disabled={!!oauthLoading}
            className="flex w-full items-center justify-center gap-3 rounded-lg border bg-background px-4 py-2.5 text-sm font-medium transition-colors hover:bg-muted dark:bg-muted dark:hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-60"
          >
            {oauthLoading === "oauth_google" ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
            ) : (
              <GoogleIcon />
            )}
            Google Workspace
          </button>
          <button
            onClick={() => void handleOAuth("oauth_microsoft")}
            disabled={!!oauthLoading}
            className="flex w-full items-center justify-center gap-3 rounded-lg border bg-background px-4 py-2.5 text-sm font-medium transition-colors hover:bg-muted dark:bg-muted dark:hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-60"
          >
            {oauthLoading === "oauth_microsoft" ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
            ) : (
              <MicrosoftIcon />
            )}
            Microsoft
          </button>
        </div>
        <p className="mt-3 text-center text-xs text-muted-foreground">
          Personal email providers such as Gmail, Outlook, Yahoo, and iCloud are not supported.
        </p>

        <div className="relative my-5">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-card px-2 text-xs text-muted-foreground">
              or continue with work email
            </span>
          </div>
        </div>

        <div
          className="px-0.5"
          onSubmitCapture={(event) => {
            markSignInStarted();
            guardWorkEmailSubmit(event, setWorkEmailError);
          }}
        >
          <SignIn
            routing="path"
            path="/sign-in"
            signUpUrl={subdomain ? "/sign-in" : "/create"}
            fallbackRedirectUrl={postAuthTarget}
            appearance={clerkEmailOnlyAppearance}
          />
          <WorkEmailError message={workEmailError} />
        </div>

        {/* Org creation / sign-up lives only on the primary app origin. On an
            org subdomain we offer sign-in to that org only. */}
        {!subdomain ? (
          <p className="mt-5 text-center text-sm text-muted-foreground">
            Don&apos;t have an account?{" "}
            <Link
              to="/create"
              className="font-medium text-foreground underline-offset-4 hover:underline"
            >
              Sign up
            </Link>
          </p>
        ) : null}
      </div>
    </div>
  );
}
